import React, { useContext, useEffect, useState } from "react";
import { useRef } from "react";
import { getTemplateHtml } from "../../Services/TemplateServices";
import parser from "html-react-parser";
import FormDataContext from "../../Contexts/FormData.context";
import TemplateOverlay from "./TemplateOverlay/TemplateOverlay";
import TemplateOverlayAction from "./TemplateOverlay/TemplateOverlayAction";
import TemplateDataContext from "../../Contexts/TemplateData.context";
import { logger } from "../../Middleware/Logger";
import axios from "axios";
import { ROOT_PATH, jsonKeyMapping } from "../../Config/Constants";
import { TemplateObject } from "../../Models/TemplateObject";
import renderFormData from "../../Util/RenderFormData";
import { WHITE_COLOR } from "../../Config/Constants";

type Props = {
  templateKey: string;
  isInnerPage: boolean;
  htmlContent?: string;
  index?: number;
  svgData?: string[];
  templatePrev?: React.RefObject<HTMLDivElement>;
};

const Template = ({
  templateKey,
  isInnerPage,
  htmlContent,
  index,
  svgData,
  templatePrev,
}: Props) => {
  const { state: templateState } = useContext(TemplateDataContext);
  const { templatePath, selectedState, templates } = templateState;
  const { state: formState, dispatch } = useContext(FormDataContext);
  const { formData, activeColor, blankFormData } = formState;
  const contentRef = useRef<HTMLDivElement | null>(null);
  /* Set the initial route to first real form step of t`he template */
  let initialStep = null;
  if (templates[templateKey] !== undefined && templates[templateKey].steps) {
    const steps = templates[templateKey].steps;
    const firstRealStep = steps.find(
      (step: any) =>
        step[jsonKeyMapping.FORM_GROUP] &&
        Array.isArray(step[jsonKeyMapping.FORM_GROUP]) &&
        step[jsonKeyMapping.FORM_GROUP].length > 0
    );
    initialStep = firstRealStep
      ? firstRealStep[jsonKeyMapping.SLUGIFY]
      : steps[0][jsonKeyMapping.SLUGIFY];
  }

  // Construct URI based on whether it's state-specific or regular template
  let uri: string;

  if (templatePath.startsWith("state-specific/")) {
    // For state-specific templates: /templatePath/state/template/stepName
    const actualTemplatePath = templatePath.replace("state-specific/", "");
    uri = `/${templatePath}/${selectedState}/${templateKey}${
      initialStep ? `/${initialStep}` : ""
    }`;
  } else {
    // For regular templates: /templatePath/template/stepName
    uri = `/${templatePath}/${templateKey}${
      initialStep ? `/${initialStep}` : ""
    }`;
  }

  const templateIndex = index && index + 1;
  const path = templatePath.replace("state-specific/", "");
  const documentName = `${path}-${templateIndex === 0 ? 1 : templateIndex}`;
  const folderPath = path + `${selectedState && `/${selectedState}`}`;
  const [formHtml, setFormHtml] = useState<string>("");

  /* load the html template */
  useEffect(() => {
    console.log("[Template] Debug Info:", {
      templateKey,
      templatePath,
      selectedState,
      isInnerPage,
      htmlContent: htmlContent ? "provided" : "undefined",
      constructedURI: uri,
      finalURI: `/${ROOT_PATH}${uri}`,
    });

    if (htmlContent === undefined) {
      const cancelToken = axios.CancelToken.source();
      const config = { cancelToken: cancelToken.token };
      const getTemplate = async (templateKey: string) => {
        try {
          const { data } = await getTemplateHtml({
            endPoint: templatePath,
            templateKey,
            config,
            state: selectedState,
          });
          setFormHtml(data);
        } catch (error) {
          if (!axios.isCancel(error)) {
            logger.log(
              "🚀 ~ file: useEffect.tsx ~ line 74 ~ getTemplate ~ error",
              error
            );
          }
        }
      };
      getTemplate(templateKey);
      return () => cancelToken.cancel();
    } else {
      setFormHtml(htmlContent);
    }
  }, [templateKey, templatePath, selectedState, htmlContent]);

  /* Set the Background of Template */

  /* Intercept anchor clicks inside rendered template and scroll within the container */
  useEffect(() => {
    const getContainer = () => {
      // prefer the passed templatePrev ref if it exists and has .current
      const passed = templatePrev as any;
      if (passed && passed.current) return passed.current as HTMLDivElement;
      // otherwise use the local content ref
      return contentRef.current as HTMLDivElement | null;
    };

    const container = getContainer();
    if (!container) return;

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = (target.closest ? target.closest('a') : null) as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('#')) return;

      const id = href.slice(1);
      if (!id) return;

      // look for the target element inside the container
      const dest = container.querySelector(`#${id}`) as HTMLElement | null;
      if (!dest) return;

      // prevent default full-page navigation
      e.preventDefault();

      // compute top offset of dest relative to container and scroll
      try {
        const containerRect = container.getBoundingClientRect();
        const destRect = dest.getBoundingClientRect();
        const offset = destRect.top - containerRect.top + container.scrollTop;
        container.scrollTo({ top: offset, behavior: 'smooth' });
      } catch (err) {
        // fallback: use element.scrollIntoView which may scroll the page
        dest.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    container.addEventListener('click', onClick);
    return () => container.removeEventListener('click', onClick);
  }, [formHtml, templatePrev]);
  useEffect(() => {
    const themeBackgroundColors =
      document.getElementsByClassName("theme-color");
    const themeFontColors = document.getElementsByClassName("theme-color-font");
    if (themeBackgroundColors && themeBackgroundColors.length > 0) {
      Array.from(themeBackgroundColors).map(
        (themeBackgroundColor: TemplateObject) => {
          // themeBackgroundColor.style.background = `#${activeColor.colorHashValue}`;
          {
            activeColor.colorHashValue === WHITE_COLOR
              ? (themeBackgroundColor.style.background = ``)
              : (themeBackgroundColor.style.background = `#${activeColor.colorHashValue}`);
          }

          {
            activeColor.colorHashValue === WHITE_COLOR
              ? (themeBackgroundColor.style.color = `#000`)
              : (themeBackgroundColor.style.color = `#fff`);
          }

          return themeBackgroundColor;
        }
      );
    }
    if (themeFontColors && themeFontColors.length > 0) {
      Array.from(themeFontColors).map((themeFontColor: TemplateObject) => {
        themeFontColor.style.color = `#${activeColor.colorHashValue}`;
        {
          activeColor.colorHashValue === WHITE_COLOR
            ? (themeFontColor.style.color = `#000`)
            : (themeFontColor.style.color = `#${activeColor.colorHashValue}`);
        }
        return themeFontColor;
      });
    }

    return () => {};
  }, [htmlContent, formHtml, activeColor]);

  /* Set the InputValues of Template */
  useEffect(() => {
    try {
      renderFormData({ formData, blankFormData, dispatch });
    } catch (error) {
      logger.error(
        "🚀 ~ file: useEffect.tsx ~ line 100 ~ setInputValues ~ error",
        error
      );
    }
    return () => {};
  }, [
    formData,
    formHtml,
    Object.keys(formData).length,
    blankFormData,
    Object.keys(blankFormData).length,
  ]);

  return (
    <>
      {svgData && svgData.length > 0 ? (
        svgData.map((item, index) => {
          return <img className="w-100" src={item} key={index} />;
        })
      ) : (
        <>
          {!isInnerPage && formHtml ? (
            <TemplateOverlay
              actionElement={
                <TemplateOverlayAction
                  documentName={documentName}
                  folderPath={folderPath}
                  uri={`/${ROOT_PATH}${uri}`}
                  actionName="Select Template"
                />
              }
            >
              <div className="thumb-box-inner">
                <div className="content-box">{parser(formHtml)}</div>
              </div>
            </TemplateOverlay>
          ) : (
            <span
              id="getHtml"
              className="page-thumbs d-none d-lg-block"
              ref={templatePrev}
            >
              {parser(formHtml)}
            </span>
          )}
        </>
      )}
    </>
  );
};

export default Template;
